import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, Link } from 'react-router-dom';
import { Container, Typography, Button, Card, CardContent, List, ListItem, Box, CircularProgress, Alert } from '@mui/material';
import { Home as HomeIcon, AdminPanelSettings } from '@mui/icons-material';
import AdminDashboard from './AdminDashboard';
import ErrorPage from './ErrorPage';
import Success from './Success';
import Loading from './Loading';

function Home() {
  return (
    <Container maxWidth="md" sx={{ mt: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <HomeIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" gutterBottom>WeCan Auth 2.0</Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Sistema OAuth per integrazione Azure AD + Rebel Net
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Button
          component={Link}
          to="/admin"
          variant="contained"
          size="large"
          startIcon={<AdminPanelSettings />}
        >
          Admin Panel
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>API Endpoints</Typography>
          <List>
            <ListItem>
              <Typography variant="body2">
                <code>POST /oauth/request-url</code> - Genera token OAuth
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                <code>GET /oauth/authorize/&#123;token&#125;</code> - Redirect Azure AD
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                <code>GET /oauth/azure/callback</code> - Callback Azure
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                <code>GET /oauth/select-account/&#123;token&#125;</code> - Selezione account
              </Typography>
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Container>
  );
}

function SelectAccount() {
  const { token } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetch(`${API_URL}/oauth/select-account/${token}`)
      .then(res => res.json())
      .then(data => {
        if (data.error) {
          setError(data.error);
        } else {
          setAccounts(data.accounts || []);
        }
        setLoading(false);
      })
      .catch(() => {
        setError('Errore di connessione');
        setLoading(false);
      });
  }, [token, API_URL]);

  const handleSelect = (username) => {
    fetch(`${API_URL}/oauth/select-account/${token}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_username: username })
    })
    .then(res => res.json())
    .then(data => {
      if (data.error) {
        setError(data.error);
        return;
      }
      if (data.cookies) {
        // Setta i cookie dal backend
        Object.entries(data.cookies).forEach(([name, value]) => {
          document.cookie = `${name}=${value}; domain=.wecanconsulting.it; path=/; max-age=28800; secure; samesite=none`;
        });
      }
      if (data.callback_url) {
        window.location.href = data.callback_url;
      }
    })
    .catch(() => setError('Errore di connessione al servizio di autenticazione'));
  };

  if (loading) return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#3F47F5'
    }}>
      <CircularProgress sx={{ color: '#FFFFFF' }} size={60} />
    </Box>
  );

  if (error) return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: '#3F47F5'
    }}>
      <Alert severity="error" sx={{ maxWidth: 400 }}>{error}</Alert>
    </Box>
  );

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#3F47F5',
      p: 2
    }}>
      <Card sx={{ 
        maxWidth: 500, 
        width: '100%',
        borderRadius: 3,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: '#3F47F5' }}>
              Seleziona Account
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight="400">
              Scegli l'account con cui accedere
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {accounts.map(account => (
              <Card
                key={account.rebelnet_username}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  border: '2px solid transparent',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    borderColor: '#3F47F5'
                  }
                }}
                onClick={() => handleSelect(account.rebelnet_username)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      background: '#3F47F5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#FFFFFF',
                      fontWeight: '700',
                      fontSize: 20
                    }}>
                      {account.rebelnet_username.charAt(0).toUpperCase()}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight="600">
                        {account.rebelnet_username}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" fontWeight="400">
                        {account.group_name || 'MANUALE'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/select-account/:token" element={<SelectAccount />} />
        <Route path="/success" element={<Success />} />
        <Route path="/loading" element={<Loading />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/error" element={<ErrorPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
